import { SortingState } from "@tanstack/react-table";
import { FullShopArticle, PurchaseStatus } from "./article";
import { PaginatedReturn, PagingParameters, sortingToUrlParam } from "./dtos";
import api from "@/lib/axiosConfig";
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

export interface FullItemOrder {
  quantity: number;
  relatedArticle: FullShopArticle;
}

export interface CustomerData {
  email: string;
  name: string;
  phone: string;

  billingAdress: {
    country: string;
    adress: string;
    postalCode: string;
    city: string;
  };
}

export interface FullShopOrder {
  id: string;

  orderNumber: number;

  priceAmount: number;

  purchaseStatus: PurchaseStatus;

  customer: CustomerData;
  paymentMethod: string;

  items: FullItemOrder[];

  issuedAt: Date;
}

export interface CustomerRecap {
  email: string;
  orderCount: number;
}

export type UpdateOrder = Pick<FullShopOrder, "purchaseStatus">;

export const countNewOrdersSince = async (sinceDate: Date) => {
  const { data } = await api.get<number>("back/api/orders/count", {
    params: {
      since: sinceDate,
    },
  });

  return data;
};

export const useCountNewOrdersSince = (sinceDate: Date) => {
  return useQuery({
    queryKey: ["newOrders", sinceDate],
    queryFn: () => countNewOrdersSince(sinceDate),
  });
};

export const getOrders = async (
  paging: PagingParameters = { page: 0, pageSize: 50 },
  sorting: SortingState = [],
  search?: string,
  customerEmail?: string,
) => {
  const { data } = await api.get<PaginatedReturn<FullShopOrder>>(
    "back/api/orders",
    {
      params: {
        ...paging,
        ...(sorting.length > 0 ? { sort: sortingToUrlParam(sorting) } : {}),
        ...(search && search.trim().length > 0
          ? { search: search.trim() }
          : {}),
        ...(customerEmail && customerEmail.trim().length > 0
          ? { customerEmail: customerEmail.trim() }
          : {}),
      },
    },
  );
  return data;
};

export const useGetOrders = (
  paging?: PagingParameters,
  sorting?: SortingState,
  search?: string,
  customerEmail?: string,
) => {
  return useQuery({
    queryKey: ["orders", paging, sorting, customerEmail, search],
    queryFn: () => getOrders(paging, sorting, search, customerEmail),
    placeholderData: keepPreviousData,
  });
};

export const updateOrder = async ({
  orderId,
  update,
}: {
  orderId: string;
  update: UpdateOrder;
}) => {
  return await api.patch(`back/api/orders/${orderId}`, update);
};

export const useUpdateOrder = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateOrder,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["orders"] });
    },
  });
};

export const getCustomers = async (
  paging: PagingParameters = { page: 0, pageSize: 50 },
  sorting: SortingState = [],
  search?: string,
) => {
  const { data } = await api.get<PaginatedReturn<CustomerRecap>>(
    "back/api/customers",
    {
      params: {
        ...paging,
        ...(sorting.length > 0 ? { sort: sortingToUrlParam(sorting) } : {}),
        ...(search && search.trim().length > 0
          ? { search: search.trim() }
          : {}),
      },
    },
  );
  return data;
};

export const useGetCustomers = (
  paging?: PagingParameters,
  sorting?: SortingState,
  search?: string,
) => {
  return useQuery({
    queryKey: ["customers", paging, sorting, search],
    queryFn: () => getCustomers(paging, sorting, search),
    placeholderData: keepPreviousData,
  });
};
